'use client';

import React from 'react';
import { Lightbulb } from 'lucide-react';

export const MakingSenseSection: React.FC = () => {
  return (
    <div className="my-16">
      <div className="flex items-center gap-3 mb-8">
        <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
          <Lightbulb className="w-6 h-6 text-purple-600" />
        </div>
        <h2 className="text-3xl lg:text-4xl font-bold text-neutral-900">
          Making Sense of Your Numbers
        </h2>
      </div>

      <div className="space-y-6">
        {/* Scenario 1 */}
        <div className="bg-gradient-to-br from-blue-50 to-white border-l-4 border-blue-500 p-6 rounded-r-lg">
          <p className="text-lg font-bold text-neutral-900 mb-3">
            If Paid Ads Only shows negative ROI:
          </p>
          <p className="text-lg text-neutral-700 leading-relaxed">
            This is normal with average execution. Your landing pages probably turn a fraction of visitors into leads instead of the 25-35% a well-optimized page reaches. Your follow-up might be slow. Your close rate on leads might be 1-2% instead of 3-5%. Small improvements at each step multiply into profitability.
          </p>
        </div>

        {/* Scenario 2 */}
        <div className="bg-gradient-to-br from-green-50 to-white border-l-4 border-green-500 p-6 rounded-r-lg">
          <p className="text-lg font-bold text-neutral-900 mb-3">
            If the Hybrid System shows massive returns:
          </p>
          <p className="text-lg text-neutral-700 leading-relaxed">
            These aren't fantasy numbers. They represent what happens when you systematically share leads with agents, maintain client relationships, nurture your database, and have infrastructure to capture organic traffic. The question isn't whether it's possible, but whether you'll build the systems to capture it. The{' '}
            <a
              href="/guide/mortgage-marketing-strategies"
              className="text-primary-600 font-semibold hover:underline"
            >
              playbook breaks down how to build each one
            </a>
            .
          </p>
        </div>

        {/* Scenario 3 */}
        <div className="bg-gradient-to-br from-purple-50 to-white border-l-4 border-purple-500 p-6 rounded-r-lg">
          <p className="text-lg font-bold text-neutral-900 mb-3">
            If your timeline shows 6+ months to profitability:
          </p>
          <p className="text-lg text-neutral-700 leading-relaxed">
            With Paid Ads Only, expect 6+ months. With the Hybrid System, profitability typically comes by Month 3-6 as channels activate. The difference? Multipliers working immediately—not whether it's possible, but whether you understand this going in or discover it $20,000 later.
          </p>
        </div>

        {/* Scenario 4 */}
        <div className="bg-gradient-to-br from-amber-50 to-white border-l-4 border-amber-500 p-6 rounded-r-lg">
          <p className="text-lg font-bold text-neutral-900 mb-3">
            If everything looks too good to be true:
          </p>
          <p className="text-lg text-neutral-700 leading-relaxed">
            Cut all the multipliers in half. Reduce conversion rates. Increase costs. See what happens with pessimistic assumptions. Even conservative projections usually show strong returns by Year 2.
          </p>
        </div>
      </div>
    </div>
  );
};
